import { useState } from "react";
import { useEditorStore } from "@/store";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { AddSourceDialog } from "./AddSourceDialog";
import { SourceEditor } from "./SourceEditor";

export function SourcesSection() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const sourcesRaw = useEditorStore((s) => s.style.sources);
  const sources: Record<string, Record<string, unknown>> = (sourcesRaw ?? {}) as Record<string, Record<string, unknown>>;
  const layers = useEditorStore((s) => s.style.layers);
  const selectedSourceId = useEditorStore((s) => s.selectedSourceId);
  const setSelectedSourceId = useEditorStore((s) => s.setSelectedSourceId);
  const removeSource = useEditorStore((s) => s.removeSource);
  const removeLayer = useEditorStore((s) => s.removeLayer);

  const sourceEntries = Object.entries(sources);
  const selectedSource = selectedSourceId ? sources[selectedSourceId] : undefined;

  const dependentLayers = pendingRemoveId
    ? layers.filter((l) => l.source === pendingRemoveId)
    : [];

  const handleConfirmRemove = () => {
    if (!pendingRemoveId) return;
    // Remove dependent layers first, then the source
    for (const layer of dependentLayers) {
      removeLayer(layer.id);
    }
    removeSource(pendingRemoveId);
    setSelectedSourceId(null);
    setPendingRemoveId(null);
  };

  return (
    <div>
      <InfoBanner storageKey="section-sources">
        Sources define where the map data comes from — vector tiles, raster imagery,
        GeoJSON files, or elevation data. Each layer references a source by ID.
        A source must exist before layers can use it.
      </InfoBanner>

      <div className="mb-3 mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-4 w-1 rounded-radius-full bg-accent" />
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-primary">
            Sources ({sourceEntries.length})
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1 rounded-radius-sm border border-accent bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-accent hover:bg-accent hover:text-accent-text transition-colors"
        >
          + Add
        </button>
      </div>

      {showAddDialog && <AddSourceDialog onClose={() => setShowAddDialog(false)} />}

      {/* Remove confirmation dialog */}
      {pendingRemoveId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPendingRemoveId(null)}>
          <div
            className="w-96 rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-text-primary">Remove source "{pendingRemoveId}"?</h2>

            {dependentLayers.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs text-text-secondary">
                  The following {dependentLayers.length} layer{dependentLayers.length > 1 ? "s" : ""} use
                  this source and will also be removed:
                </p>
                <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto">
                  {dependentLayers.map((l) => (
                    <li
                      key={l.id}
                      className="flex items-center gap-2 rounded-radius-sm bg-error-muted px-2 py-1 text-xs"
                    >
                      <span className="font-mono text-[10px] text-text-muted">{l.type}</span>
                      <span className="text-text-primary">{l.id}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="mt-3 text-xs text-text-secondary">
                No layers reference this source.
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingRemoveId(null)}
                className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                className="flex items-center gap-1 rounded-radius-sm border border-error/30 bg-error px-3 py-1.5 text-xs font-medium text-white hover:bg-error/90 transition-colors"
              >
                ✕ Remove{dependentLayers.length > 0 ? ` source + ${dependentLayers.length} layer${dependentLayers.length > 1 ? "s" : ""}` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Source list */}
      {sourceEntries.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="text-2xl text-text-muted">◉</div>
          <div className="text-xs text-text-secondary">No sources</div>
          <p className="max-w-48 text-[10px] text-text-muted">
            Sources provide map data — vector tiles, raster imagery, or GeoJSON. Add one to start building layers.
          </p>
        </div>
      ) : (
      <div className="space-y-0.5">
        {sourceEntries.map(([id, src]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSelectedSourceId(id)}
            className={`flex w-full items-center gap-2 rounded-radius-sm px-2 py-1.5 text-left text-xs transition-colors ${
              selectedSourceId === id
                ? "bg-accent-muted text-accent"
                : "text-text-primary hover:bg-surface-sunken"
            }`}
          >
            <span className="rounded-radius-sm bg-surface-sunken px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
              {(src.type as string) ?? "?"}
            </span>
            <span className="flex-1 truncate font-medium">{id}</span>
          </button>
        ))}
      </div>
      )}

      {/* Selected source detail */}
      {selectedSourceId && selectedSource && (
        <div className="mt-spacing-section border-t border-border-subtle pt-spacing-section">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-medium text-text-secondary">
              {selectedSourceId}
            </h3>
            <button
              type="button"
              onClick={() => setPendingRemoveId(selectedSourceId)}
              className="flex items-center gap-1 rounded-radius-sm border border-error/30 bg-error-muted px-2.5 py-1 text-[11px] font-medium text-error hover:bg-error hover:text-white transition-colors"
            >
              ✕ Remove
            </button>
          </div>

          <SourceEditor sourceId={selectedSourceId} source={selectedSource} />
        </div>
      )}
    </div>
  );
}
